import {
  MenuCommand,
  MenuItemOptionProps,
  StyledMenuItemProps,
  useMenuOption,
} from '@chakra-ui/menu';
import { MaybeRenderProp } from '@chakra-ui/react-utils';
import { chakra, forwardRef, SystemStyleObject, useMultiStyleConfig } from '@chakra-ui/system';
import { Checkbox } from '@lego/klik-ui-checkbox';
import { CheckTickBold } from '@lego/klik-ui-icons';
import { Radio } from '@lego/klik-ui-radio';
import * as React from 'react';
import {
  IDropdownSelectionIndicatorAlignment,
  IDropdownSelectionIndicatorType,
  IDropdownSizes,
} from './DropdownTypes';

// add the 'inline-block' ::before element to our unchecked radios
// to get propper vertical alignment
const radioOffStyles: SystemStyleObject = {
  _before: {
    content: `""`,
    display: 'inline-block',
    pos: 'relative',
    w: 2.5,
    h: 2.5,
    borderRadius: '50%',
  },
};

const RadioOn = <Radio isChecked={true} />;
const RadioOff = <Radio sx={radioOffStyles} />;

const CheckOn = <Checkbox isChecked={true} />;
const CheckOff = <Checkbox />;

// was: IMenuItemOptionProps
export type IDropdownOptionProps = MenuItemOptionProps & {
  text?: string;
  size?: IDropdownSizes;
  selectionindicator?: IDropdownSelectionIndicatorType;
  selectionindicatoralignment?: IDropdownSelectionIndicatorAlignment;
  isMultiple?: boolean;
  children: MaybeRenderProp<{
    isChecked?: boolean;
    isDisabled?: boolean;
    value?: string;
    label?: string;
    type?: 'checkbox' | 'radio';
  }>;
};

const StyledDropdownOption = forwardRef<StyledMenuItemProps, 'button'>((props, ref) => {
  const styles = useMultiStyleConfig('Dropdown', props);

  return <chakra.button ref={ref} type="button" {...props} __css={styles.item} />;
});

// was: MenuItemOption
export const DropdownOption = forwardRef<IDropdownOptionProps, 'button'>((props, ref) => {
  const {
    size,
    command,
    commandSpacing,
    icon = <CheckTickBold />,
    selectionindicator: selectionIndicator,
    selectionindicatoralignment: selectionIndicatorAlignment,
  } = props;

  const optionProps = useMenuOption(props, ref) as StyledMenuItemProps;
  const styles = useMultiStyleConfig('Dropdown', props);

  const decoratorVerticalAlignmentOverride: SystemStyleObject = {
    alignItems: selectionIndicatorAlignment,
  };

  // Options below a section/group with a title should be indented (e.g. pad left)
  // but only if their selectionIndicator is tick or none. radio or check stay put.
  // Note: doing it here, as the Theme dont know about $selectionIndicator
  const shouldIndent = selectionIndicator === 'tick' || selectionIndicator === 'none';
  // const indentPaddingScale = { sm: 0.5, md: 1, lg: 2 };
  const indentPaddingScale = { sm: 0.25, md: 0.5, lg: 0.75 };
  const indentPadding = `${indentPaddingScale[size ?? 'md']}rem`;

  const indentStyles: SystemStyleObject = {
    '[data-klik-indent="true"] > button &, [data-klik-indent="true"] ~ button &': {
      paddingInlineStart: shouldIndent ? indentPadding : 0,
    },
  };

  const render = React.useMemo(() => {
    return (
      <chakra.span
        sx={{
          ...styles.item_decorator_container,
          ...decoratorVerticalAlignmentOverride,
          ...indentStyles,
        }}
        textStyle={size}
      >
        {selectionIndicator === 'radio' && (
          <chakra.div sx={styles.item_decorator_front}>
            {props.isChecked ? RadioOn : RadioOff}
          </chakra.div>
        )}

        {selectionIndicator === 'check' && (
          <chakra.div sx={styles.item_decorator_front}>
            {props.isChecked ? CheckOn : CheckOff}
          </chakra.div>
        )}

        <chakra.span className="opt-wrap" sx={styles.item_decorator_content}>
          {optionProps.children}
        </chakra.span>

        {command && <MenuCommand marginStart={commandSpacing}>{command}</MenuCommand>}

        {selectionIndicator === 'tick' && (
          <chakra.div
            opacity={props.isChecked ? 1 : 0}
            sx={{
              ...styles.item_decorator_end,
              ...decoratorVerticalAlignmentOverride,
            }}
          >
            {icon}
          </chakra.div>
        )}
      </chakra.span>
    );
  }, [
    optionProps.children,
    props.isChecked,
    props.value,
    props.text,
    props.isDisabled,
    props.isMultiple,
    props.selectionindicator,
    props.selectionindicatoralignment,
  ]);

  return <StyledDropdownOption {...optionProps}>{render}</StyledDropdownOption>;
});

DropdownOption.id = 'MenuItemOption';
